import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import Dropdown, { DropdownOption } from '../Dropdown';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import {
  channelPicker,
  fetchChannelPickerData,
  setSelectedChannelAndCountry,
} from '../../Reducers/ChannelPicker.reducer';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import LoadingSpinner from '../LoadingSpinner';

const LanguageSelectModal = () => {
  const {
    items: channelPickerItems = [],
    currentChannel,
    countries,
    currentCountryCode,
    isLoading,
    open,
  } = useTypedSelector((state) => state.channelPicker);
  const dispatch = useTypedDispatch();

  const [selectedChannelSystemId, setSelectedChannelSystemId] =
    useState<string>(currentChannel.channelSystemId);
  const [selectedCountryCode, setSelectedCountryCode] =
    useState<string>(currentCountryCode);

  const channelOptions: DropdownOption[] = useMemo(
    () =>
      channelPickerItems?.map((item) => ({
        label: item.name,
        value: item.channelSystemId,
        disabled: selectedChannelSystemId == item.channelSystemId,
      })) ?? [],
    [channelPickerItems, selectedChannelSystemId]
  );

  const countryOptions: DropdownOption[] = useMemo(
    () =>
      countries?.map((item) => ({
        label: item.name,
        value: item.countryCode,
        disabled: selectedCountryCode == item.countryCode,
      })) ?? [],
    [countries, selectedCountryCode]
  );

  const onSubmit = () => {
    const selectedChannelItem = channelPickerItems.find(
      (o) => o.channelSystemId == selectedChannelSystemId
    );
    if (!selectedChannelItem) return;

    dispatch(
      setSelectedChannelAndCountry({
        channelItem: selectedChannelItem,
        countryCode: selectedCountryCode,
      })
    );
  };

  useEffect(() => {
    dispatch(
      addOverlay(
        'language-select-modal',
        () => dispatch(channelPicker.setOpen(false)),
        0
      )
    );

    return () => {
      dispatch(removeOverlay('language-select-modal'));
    };
  }, []);

  useEffect(() => {
    if (!open || !selectedCountryCode) return;

    dispatch(fetchChannelPickerData(selectedCountryCode));
  }, [selectedCountryCode]);

  useEffect(() => {
    if (channelOptions.length == 1) {
      setSelectedChannelSystemId(channelOptions[0]!.value);
    }
  }, [channelOptions]);

  return (
    <div className="flex flex-col p-4">
      <h5 className="mb-3">
        {translate('language-select.choose-country')}
      </h5>
      <div className="relative mb-4">
        <Dropdown
          onChange={({ value }) => setSelectedCountryCode(value)}
          options={countryOptions}
          selectedValue={selectedCountryCode}
          size="large"
        />
        <label
          className="absolute -top-2 left-[14px] bg-white px-1 text-sm font-light text-gray-700"
          htmlFor="country"
        >
          {translate('language-select.country')}
        </label>
      </div>
      <h5 className="mb-3">
        {translate('language-select.choose-language')}
      </h5>
      <div className="relative mb-4">
        <Dropdown
          onChange={({ value }) => setSelectedChannelSystemId(value)}
          options={channelOptions}
          selectedValue={selectedChannelSystemId}
          size="large"
        />
        <label
          className="absolute -top-2 left-[14px] bg-white px-1 text-sm font-light text-gray-700"
          htmlFor="country"
        >
          {translate('language-select.language')}
        </label>
      </div>
      <button
        onClick={onSubmit}
        className="btn btn--large btn--black btn--fullwidth"
        disabled={
          (currentChannel.channelSystemId == selectedChannelSystemId &&
            currentCountryCode == selectedCountryCode) ||
          !selectedChannelSystemId ||
          isLoading
        }
      >
        {!isLoading && translate('general.update')}
        {isLoading && <LoadingSpinner />}
      </button>
    </div>
  );
};

export default LanguageSelectModal;
